import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  createDeliveryDriver,
  deleteDeliveryDriver,
  getDeliveryDrivers,
  patchDeliveryDriverActive,
  updateDeliveryDriver,
} from '../lib/api';
import { toast } from 'sonner';

const emptyForm = { name: '', phone: '' };

function nextDriverCodePreview(drivers) {
  let maxN = 0;
  for (const d of drivers) {
    const digits = String(d.driver_code || '').replace(/\D/g, '');
    if (digits) maxN = Math.max(maxN, parseInt(digits, 10) || 0);
  }
  return String(maxN + 1).padStart(2, '0');
}

export default function AdminDeliveryDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const nextCode = useMemo(() => nextDriverCodePreview(drivers), [drivers]);

  const load = useCallback(async () => {
    try {
      const data = await getDeliveryDrivers();
      setDrivers(Array.isArray(data) ? data : []);
    } catch {
      toast.error('No se pudieron cargar los repartidores');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name || '',
      phone: d.phone || '',
    });
  };

  const save = async () => {
    if (!form.name.trim() || form.phone.replace(/\D/g, '').length < 10) {
      toast.error('Completa nombre y teléfono (mínimo 10 dígitos)');
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateDeliveryDriver(editingId, {
          name: form.name.trim(),
          phone: form.phone.trim(),
        });
        toast.success('Repartidor actualizado');
      } else {
        const created = await createDeliveryDriver({
          name: form.name.trim(),
          phone: form.phone.trim(),
          active: true,
        });
        toast.success(`Repartidor creado con código ${created.driver_code || nextCode}`);
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d, checked) => {
    try {
      setTogglingId(d.id);
      await patchDeliveryDriverActive(d.id, checked);
      setDrivers((prev) =>
        prev.map((row) => (row.id === d.id ? { ...row, active: checked } : row)),
      );
      toast.success(checked ? 'Repartidor activado' : 'Repartidor desactivado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo cambiar el estado');
    } finally {
      setTogglingId(null);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`¿Eliminar repartidor ${d.driver_code} — ${d.name}?`)) return;
    try {
      await deleteDeliveryDriver(d.id);
      toast.success('Repartidor eliminado');
      if (editingId === d.id) resetForm();
      await load();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const editingDriver = editingId ? drivers.find((d) => d.id === editingId) : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Editar repartidor' : 'Nuevo repartidor'}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 max-w-md">
          {editingId ? (
            <div>
              <Label>Código</Label>
              <Input value={editingDriver?.driver_code || ''} disabled className="font-mono bg-muted" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Código consecutivo al guardar: <span className="font-mono font-semibold text-foreground">{nextCode}</span>
            </p>
          )}
          <div>
            <Label>Nombre</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <Label>Teléfono (WhatsApp)</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              inputMode="tel"
              autoComplete="tel"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving}>
              {saving ? 'Guardando…' : editingId ? 'Actualizar' : 'Crear'}
            </Button>
            {editingId ? (
              <Button variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Repartidores</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Activo</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {drivers.map((d) => (
                <TableRow key={d.id} className={d.active === false ? 'opacity-60' : undefined}>
                  <TableCell className="font-mono">{d.driver_code}</TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>{d.phone}</TableCell>
                  <TableCell>
                    <Switch
                      checked={d.active !== false}
                      disabled={togglingId === d.id}
                      onCheckedChange={(checked) => toggleActive(d, checked)}
                      aria-label={d.active !== false ? 'Desactivar repartidor' : 'Activar repartidor'}
                    />
                  </TableCell>
                  <TableCell className="space-x-1">
                    <Button size="sm" variant="outline" onClick={() => startEdit(d)}>
                      Editar
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => remove(d)}>
                      Eliminar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
